import React from 'react';
import { useSparkStepByID } from 'hooks/SparkBuilder/sparkStep';
import { useSparkStepReferences } from 'hooks/SparkBuilder/sparkStepReferences';
import { useSparkBuilderStore } from 'stores';
import { ResolvedValues, SparkReferenceValue } from 'types/SparkStepReference';
import { MappedFieldPill } from './MappedPill';

interface DetectedReferences {
  pillContent: string;
  pillLength: number;
  pillStartIndex: number;
}
export const detectReferences = (content: string): DetectedReferences[] => {
  const regex = /{{\s*([^\s{}]+?)\s*}}/g;
  const pills: DetectedReferences[] = [];

  for (const match of `${content}`?.matchAll(regex)) {
    const pillLength = match[0].length;
    const rawContent = match[1];

    pills.push({
      pillContent: rawContent.trimStart(),
      pillLength,
      pillStartIndex: match.index!,
    });
  }

  return pills;
};

export const replaceReferences = (
  content: string,
  getReplacement: (pillContent: string) => any
): React.ReactNode => {
  const pills = detectReferences(content);
  let updatedContent: React.ReactNode[] = [];
  let lastIndex = 0;

  const updatedContentWithLineBreks = (text: string) => {
    const lines = text.split('\n');
    lines.forEach((line, index) => {
      updatedContent.push(line);
      if (index < lines.length - 1) {
        updatedContent.push(<br key={`br-${updatedContent.length}`} />);
      }
    });
  };

  pills.forEach(({ pillContent, pillStartIndex, pillLength }) => {
    const replacement = getReplacement(pillContent) || '';

    if (pillStartIndex > lastIndex) {
      updatedContentWithLineBreks(content.slice(lastIndex, pillStartIndex));
    }

    updatedContent.push(replacement);

    lastIndex = pillStartIndex + pillLength;
  });

  if (lastIndex < content.length) {
    updatedContentWithLineBreks(content.slice(lastIndex));
  }

  return updatedContent;
};

export function useTransformPromptReferences() {
  const selectedStep = useSparkBuilderStore(
    (store) => store.builderSideBar.selectedStep
  );
  const { sparkSteps, sparkInputParameters } = useSparkStepReferences(
    selectedStep?.id
  );
  const { sparkStep } = useSparkStepByID(selectedStep?.id);

  const joinedReferenceValues: SparkReferenceValue[] = React.useMemo(() => {
    const stepsResolvedValues: SparkReferenceValue[] | undefined = sparkSteps
      ?.map((step) => {
        return step.meta.referenceValues.map((value) => ({
          ...value,
          stepSortOrder: step.stepOrder,
        }));
      })
      .flatMap((item) => item);

    const inputParameterResolvedValues: SparkReferenceValue[] | undefined =
      sparkInputParameters
        ?.map((parameter) => {
          return parameter.meta.referenceValues.map((value) => ({
            ...value,
          }));
        })
        .flatMap((item) => item);
    const joinedRef = [
      ...(stepsResolvedValues || []),
      ...(inputParameterResolvedValues || []),
    ];
    return joinedRef;
  }, [sparkInputParameters, sparkSteps]);

  const replacer = (pillContent: string) =>
    getReplacement(
      pillContent,
      joinedReferenceValues,
      sparkStep?.meta.resolvedValues
    );

  return (promptString: string) => replaceReferences(promptString, replacer);
}

function getReplacement(
  pillContent: string,
  joinedReferenceValues: SparkReferenceValue[],
  resolvedValues: ResolvedValues[] | undefined
) {
  const reference = joinedReferenceValues?.find((ref) => {
    const reference = ref.reference;
    const trimmedReference: string = reference?.slice(2, -2);
    return pillContent === trimmedReference;
  });

  const resolvedValue = resolvedValues?.find((val) => {
    const reference = val.reference;
    const trimmedReference: string = reference?.slice(2, -2);
    return pillContent === trimmedReference;
  });

  if (resolvedValue) {
    const props = {
      appIconSrc: resolvedValue.iconURL,
      label: resolvedValue.value,
      referenceValue: resolvedValue.value,
      resource: resolvedValue?.key,
      isInvalid: !!resolvedValue.error,
      errorLabel: resolvedValue.error?.title || '',
      stepSortOrder: null,
    };
    return <MappedFieldPill {...props} />;
  } else if (reference) {
    const props = {
      appIconSrc: reference.iconURL,
      label: reference.value,
      referenceValue: reference.value,
      resource: reference.key,
      isInvalid: !!reference.error,
      errorLabel: '',
      stepSortOrder: null,
    };
    return <MappedFieldPill {...props} />;
  }
}
